import { Build, SvgIconComponent } from "@mui/icons-material"
import { IconButton, Menu, MenuItem, SxProps, Tooltip, TooltipProps, Typography } from "@mui/material"
import React, { useState } from "react"
import { OptionType } from "../../store/types/OptionType"

interface ToolProps<T extends OptionType> {
    color?: string
    disabled?: boolean
    Icon?: SvgIconComponent
    onChange: (option?: T) => void
    options?: T[]
    tooltip?: string
    TooltipProps?: Partial<TooltipProps>
    value?: boolean
    sx?: SxProps
    dataTestId?: string
}

export const Tool = <T extends OptionType>(props: ToolProps<T>) => {
    const {
        color,
        disabled = false,
        Icon = Build,
        onChange,
        options = [],
        tooltip = "",
        TooltipProps,
        value = true,
        sx = [],
        dataTestId,
    } = props
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const open = Boolean(anchorEl)

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (options && options.length > 0) {
            setAnchorEl(event.currentTarget)
        } else {
            onChange()
        }
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleMenuChange = (option: T) => {
        onChange(option)
        handleClose()
    }

    return (
        <>
            <Tooltip title={tooltip} arrow {...TooltipProps}>
                <span>
                    <IconButton
                        disabled={disabled}
                        onClick={handleClick}
                        sx={[...(Array.isArray(sx) ? sx : [sx]), color && { color }]}
                        data-testid={dataTestId}
                    >
                        <Icon color={value && !disabled ? "inherit" : "disabled"} />
                    </IconButton>
                </span>
            </Tooltip>
            {options.length > 0 && (
                <Menu onClose={handleClose} open={open} transitionDuration={0} anchorEl={anchorEl}>
                    {options.map((option, index: number) => {
                        let label
                        let tooltip = ""
                        let disabled = false

                        if (typeof option === "string") {
                            label = option
                        } else {
                            label = option.label
                            tooltip = option.tooltip || ""
                            disabled = Boolean(option.disabled)
                        }

                        const item = (
                            <MenuItem
                                key={index}
                                disabled={disabled}
                                onClick={() => !disabled && handleMenuChange(option)}
                            >
                                {label}
                            </MenuItem>
                        )

                        if (tooltip) {
                            return (
                                <Tooltip key={index} title={<Typography variant="body2">{tooltip}</Typography>} arrow>
                                    {item}
                                </Tooltip>
                            )
                        }

                        return item
                    })}
                </Menu>
            )}
        </>
    )
}
